import React, { useEffect, useState } from 'react';
import RightPanel from '../../../components/RightPanel';
import { projectsApi } from '../../../services/api';
import { ProjectDetail } from '../../../types';
import { ExternalLink, Edit2, X, Save } from 'lucide-react';
import { showSuccess, showError } from '../../../utils/toast';
import Skeleton from '../../../components/Skeleton';
import Badge from '../../../components/ui/Badge';
import { projectStatusVariant } from '../../../utils/badgeVariants';

interface ProjectSidePanelProps {
  projectId: number;
  onClose: () => void;
  onUpdate?: () => void;
  initialEditMode?: boolean;
}

const ProjectSidePanel: React.FC<ProjectSidePanelProps> = ({ projectId, onClose, onUpdate, initialEditMode = false }) => {
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(initialEditMode);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [form, setForm] = useState({
    name: '' as string,
    drive_folder_url: '' as string,
    status: '' as string,
    start_date: '' as string,
    end_date: '' as string,
    project_description: '' as string,
  });

  // Reset edit mode when projectId or initialEditMode changes
  useEffect(() => {
    setEditMode(initialEditMode);
  }, [projectId, initialEditMode]);

  // Load project data
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await projectsApi.getById(projectId);
        setProject(data);
        
        // Initialize form with project data
        setForm({
          name: data.name || '',
          drive_folder_url: data.drive_folder_url || '',
          status: data.status || 'Planning',
          start_date: data.start_date ? new Date(data.start_date).toISOString().split('T')[0] : '',
          end_date: data.end_date ? new Date(data.end_date).toISOString().split('T')[0] : '',
          project_description: data.project_description || '',
        });
      } catch (error) {
        console.error('Failed to load project:', error);
        setError('Failed to load project');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [projectId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!form.name.trim()) {
      setError('Project name is required');
      return;
    }

    if (form.start_date && form.end_date && new Date(form.end_date) < new Date(form.start_date)) {
      setError('End date must be after start date');
      return;
    }
    
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    
    try {
      const response = await projectsApi.update(projectId, {
        name: form.name,
        drive_folder_url: form.drive_folder_url || null,
        status: form.status || 'Planning',
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        project_description: form.project_description || null,
      });

      // Reload project data
      const updatedData = await projectsApi.getById(projectId);
      setProject(updatedData);
      
      // Show success toast
      showSuccess('Project updated successfully!');
      
      // Switch back to view mode
      setEditMode(false);
      
      // Refresh list if callback provided
      if (onUpdate) {
        onUpdate();
      }
    } catch (e: any) {
      const errorMessage = e?.response?.data?.message || e?.message || 'Update failed';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Reset form to original project data
    if (project) {
      setForm({
        name: project.name || '',
        drive_folder_url: project.drive_folder_url || '',
        status: project.status || 'Planning',
        start_date: project.start_date ? new Date(project.start_date).toISOString().split('T')[0] : '',
        end_date: project.end_date ? new Date(project.end_date).toISOString().split('T')[0] : '',
        project_description: project.project_description || '',
      });
    }
    setEditMode(false);
    setError(null);
  };

  const header = (
    <div className="min-w-0">
      <p className="text-xs theme-text-secondary font-mono truncate">
        PROJECT-{projectId}
      </p>
      <h3 className="text-base font-semibold theme-text-primary truncate">
        {project?.name || 'Loading…'}
      </h3>
    </div>
  );

  const headerExtras = project && (
    <div className="flex items-center gap-2">
      {!editMode ? (
        <button
          onClick={() => setEditMode(true)}
          className="inline-flex items-center gap-1.5 px-2 py-1 text-sm rounded theme-border theme-bg-secondary hover:opacity-80 transition-opacity theme-text-primary"
          title="Edit project"
        >
          <Edit2 className="w-3.5 h-3.5" />
          Edit
        </button>
      ) : (
        <div className="flex items-center gap-2">
          <button
            onClick={handleCancel}
            disabled={submitting}
            className="inline-flex items-center gap-1.5 px-2 py-1 text-sm rounded theme-border theme-bg-secondary hover:opacity-80 transition-opacity disabled:opacity-50 theme-text-primary"
            title="Cancel editing"
          >
            <X className="w-3.5 h-3.5" />
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !form.name.trim() || (!!form.start_date && !!form.end_date && new Date(form.end_date) < new Date(form.start_date))}
            className="inline-flex items-center gap-1.5 px-2 py-1 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50"
            title="Save changes"
          >
            <Save className="w-3.5 h-3.5" />
            {submitting ? 'Saving...' : 'Save'}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <RightPanel isOpen={true} onClose={onClose} title={header} headerExtras={headerExtras}>
      {loading ? (
        <div className="space-y-3 p-1">
          <Skeleton height={60} variant="rounded" />
          <div className="grid grid-cols-2 gap-3">
            <Skeleton height={48} variant="rounded" />
            <Skeleton height={48} variant="rounded" />
          </div>
          <Skeleton height={48} variant="rounded" />
          <Skeleton height={48} variant="rounded" />
          <Skeleton height={48} variant="rounded" />
          <Skeleton height={48} variant="rounded" />
        </div>
      ) : project ? (
        <div className="space-y-4">
          {error && (
            <div className="p-2 rounded bg-red-50 text-red-700 border border-red-200 text-sm">
              {error}
            </div>
          )}

          {editMode ? (
            // Edit Form
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium theme-text-primary mb-0.5">Project Name <span className='text-red-500'>*</span></label>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className="w-full theme-border rounded px-2 py-1 text-sm theme-bg-primary theme-text-primary"
                  required
                />
              </div>

              {/* Customer - Read Only */}
              <div>
                <label className="block text-sm font-medium theme-text-primary mb-0.5">Customer</label>
                <div className="w-full theme-border rounded px-2 py-1 text-sm theme-bg-secondary theme-text-secondary">
                  {project.customer ? `${project.customer.item} (${project.customer.client_id})` : 'N/A'}
                </div>
                <p className="text-xs theme-text-tertiary mt-0.5">Customer cannot be changed (set from deal)</p>
              </div>

              {/* Job Board Link - Read Only */}
              {project.job_board_link && (
                <div>
                  <label className="block text-sm font-medium theme-text-primary mb-0.5">Job Board Link</label>
                  <div className="w-full theme-border rounded px-2 py-1 text-sm theme-bg-secondary theme-text-secondary">
                    <a
                      href={project.job_board_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
                    >
                      <ExternalLink className="w-3 h-3" />
                      {project.job_board_link}
                    </a>
                  </div>
                  <p className="text-xs theme-text-tertiary mt-0.5">Job Board Link is system-generated and cannot be changed</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium theme-text-primary mb-0.5">Status <span className='text-red-500'>*</span></label>
                <select
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                  className="w-full theme-border rounded px-2 py-1 text-sm theme-bg-primary theme-text-primary"
                  required
                >
                  <option value="Planning">Planning</option>
                  <option value="Active">Active</option>
                  <option value="In Progress">In Progress</option>
                  <option value="On Track">On Track</option>
                  <option value="Delayed">Delayed</option>
                  <option value="In Testing">In Testing</option>
                  <option value="On Hold">On Hold</option>
                  <option value="Approved">Approved</option>
                  <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                  <option value="Invoiced">Invoiced</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium theme-text-primary mb-0.5">Start Date</label>
                <input
                  type="date"
                  name="start_date"
                  value={form.start_date}
                  onChange={handleChange}
                  className="w-full theme-border rounded px-2 py-1 text-sm theme-bg-primary theme-text-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium theme-text-primary mb-0.5">End Date</label>
                <input
                  type="date"
                  name="end_date"
                  value={form.end_date}
                  onChange={handleChange}
                  min={form.start_date || undefined}
                  className="w-full theme-border rounded px-2 py-1 text-sm theme-bg-primary theme-text-primary"
                />
                {form.start_date && form.end_date && new Date(form.end_date) < new Date(form.start_date) && (
                  <p className="text-xs text-red-600 mt-0.5">End date must be after start date</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium theme-text-primary mb-0.5">Project Description</label>
                <textarea
                  name="project_description"
                  value={form.project_description}
                  onChange={handleChange}
                  rows={4}
                  className="w-full theme-border rounded px-2 py-1 text-sm theme-bg-primary theme-text-primary"
                  placeholder="Enter project description..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium theme-text-primary mb-0.5">Drive Folder URL</label>
                <input
                  type="url"
                  name="drive_folder_url"
                  value={form.drive_folder_url}
                  onChange={handleChange}
                  className="w-full theme-border rounded px-2 py-1 text-sm theme-bg-primary theme-text-primary"
                  placeholder="https://..."
                />
                {form.drive_folder_url && (
                  <a
                    href={form.drive_folder_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:text-blue-800 mt-1 inline-flex items-center gap-1"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Open link
                  </a>
                )}
              </div>
            </form>
          ) : (
            // View Mode
            <>
              {/* Project Details */}
              <div className="rounded-xl theme-border theme-card p-3">
                <h4 className="text-sm font-semibold theme-text-primary mb-3">Project Details</h4>
                <div className="space-y-2">
                  <div>
                    <p className="text-xs theme-text-secondary">Project Number</p>
                    <p className="text-sm theme-text-primary font-medium">{project.project_number}</p>
                  </div>
                  <div>
                    <p className="text-xs theme-text-secondary">Project Name</p>
                    <p className="text-sm theme-text-primary font-medium">{project.name}</p>
                  </div>
                  <div>
                    <p className="text-xs theme-text-secondary">Status</p>
                    {project.status ? (
                      <Badge variant={projectStatusVariant(project.status)}>{project.status}</Badge>
                    ) : (
                      <p className="text-sm theme-text-tertiary">Not set</p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs theme-text-secondary">Customer</p>
                    <p className="text-sm theme-text-primary">
                      {project.customer ? `${project.customer.item} (${project.customer.client_id})` : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Dates */}
              {(project.start_date || project.end_date) && (
                <div className="rounded-xl theme-border theme-card p-3">
                  <h4 className="text-xs font-semibold theme-text-secondary mb-2">Dates</h4>
                  <div className="space-y-1">
                    {project.start_date && (
                      <div className="flex justify-between text-xs">
                        <span className="theme-text-secondary">Start Date</span>
                        <span className="theme-text-primary">{new Date(project.start_date).toLocaleDateString()}</span>
                      </div>
                    )}
                    {project.end_date && (
                      <div className="flex justify-between text-xs">
                        <span className="theme-text-secondary">End Date</span>
                        <span className="theme-text-primary">{new Date(project.end_date).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Project Description */}
              {project.project_description && (
                <div className="rounded-xl theme-border theme-card p-3">
                  <h4 className="text-xs font-semibold theme-text-secondary mb-2">Project Description</h4>
                  <p className="text-sm theme-text-primary whitespace-pre-wrap">{project.project_description}</p>
                </div>
              )}

              {/* Deal Information */}
              {project.deal && (
                <div className="rounded-xl theme-border theme-card p-3">
                  <h4 className="text-xs font-semibold theme-text-secondary mb-2">Associated Deal</h4>
                  <div className="space-y-1">
                    <p className="text-sm theme-text-primary font-medium">{project.deal.name}</p>
                    <p className="text-xs theme-text-secondary">Stage: {project.deal.stage}</p>
                  </div>
                </div>
              )}

              {/* Links */}
              <div className="rounded-xl theme-border theme-card p-3">
                <h4 className="text-xs font-semibold theme-text-secondary mb-2">Links</h4>
                <div className="space-y-2">
                  {project.job_board_link ? (
                    <a
                      href={project.job_board_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Job Board
                    </a>
                  ) : (
                    <p className="text-sm theme-text-tertiary">No job board link</p>
                  )}
                  {project.drive_folder_url ? (
                    <a
                      href={project.drive_folder_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Drive Folder
                    </a>
                  ) : (
                    <p className="text-sm theme-text-tertiary">No drive folder link</p>
                  )}
                </div>
              </div>

              {/* Timestamps */}
              <div className="rounded-xl theme-border theme-card p-3">
                <h4 className="text-xs font-semibold theme-text-secondary mb-2">Timeline</h4>
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="theme-text-secondary">Created</span>
                    <span className="theme-text-primary">{new Date(project.created_at).toLocaleString()}</span>
                  </div>
                  {project.updated_at && (
                    <div className="flex justify-between text-xs">
                      <span className="theme-text-secondary">Updated</span>
                      <span className="theme-text-primary">{new Date(project.updated_at).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="text-sm theme-text-tertiary">Project not found</div>
      )}
    </RightPanel>
  );
};

export default ProjectSidePanel;

